import { useState } from 'react';

const tools = [
  {
    icon: '🤖',
    name: 'ChatGPT Plus',
    badge: 'Plan Anual',
    tagline: 'Tu estratega de IA personal.',
    description:
      'Accede al modelo GPT-4o más avanzado. Genera contenido, estrategias de marketing, scripts de ventas y automatiza tu negocio con la IA más poderosa del mundo.',
    features: ['GPT-4o ilimitado', 'Generación de imágenes DALL·E', 'Análisis de datos avanzado', 'Plugins y herramientas'],
    color: 'from-[#10b981] to-[#059669]',
    glow: 'rgba(16,185,129,0.3)',
    borderHover: '#10b981',
    img: 'https://readdy.ai/api/search-image?query=artificial%20intelligence%20neural%20network%20glowing%20green%20neon%20abstract%20digital%20brain%20on%20dark%20background%20minimalist%20tech%20aesthetic&width=400&height=300&seq=10&orientation=landscape',
  },
  {
    icon: '🎬',
    name: 'CapCut PRO',
    badge: 'Plan Anual',
    tagline: 'Edición de video de alto impacto.',
    description:
      'Crea reels, shorts y videos virales con efectos profesionales, subtítulos automáticos con IA y plantillas premium que convierten vistas en ventas.',
    features: ['Subtítulos IA automáticos', 'Efectos y transiciones PRO', 'Eliminación de fondo', 'Exportación 4K sin marca'],
    color: 'from-[#8b5cf6] to-[#7c3aed]',
    glow: 'rgba(139,92,246,0.3)',
    borderHover: '#8b5cf6',
    img: 'https://readdy.ai/api/search-image?query=video%20editing%20professional%20software%20interface%20glowing%20purple%20neon%20dark%20background%20cinematic%20aesthetic%20modern&width=400&height=300&seq=11&orientation=landscape',
  },
  {
    icon: '🎥',
    name: 'Filmora PRO',
    badge: 'Última versión / 1 año',
    tagline: 'Calidad cinematográfica.',
    description:
      'El editor de video favorito de los creadores profesionales. Efectos de Hollywood, corrección de color avanzada y herramientas de IA para producción de nivel agencia.',
    features: ['Efectos cinematográficos', 'Corrección de color IA', 'Audio mastering', 'Plantillas premium'],
    color: 'from-[#ec4899] to-[#be185d]',
    glow: 'rgba(236,72,153,0.3)',
    borderHover: '#ec4899',
    img: 'https://readdy.ai/api/search-image?query=cinematic%20video%20production%20professional%20editing%20dark%20studio%20glowing%20pink%20neon%20lights%20modern%20aesthetic&width=400&height=300&seq=12&orientation=landscape',
  },
];

export default function Beneficios() {
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <section id="beneficios" className="relative bg-[#0a0a0a] py-20 md:py-32 px-4">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-[#8b5cf6]/30 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-20">
          <span className="inline-block bg-[#8b5cf6]/10 border border-[#8b5cf6]/30 text-[#c4b5fd] text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-5">
            Lo que incluye tu pack
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-black text-white leading-tight">
            Mira{' '}
            <span className="bg-gradient-to-r from-[#8b5cf6] to-[#10b981] bg-clip-text text-transparent">
              todo lo que te llevas
            </span>{' '}
            hoy
          </h2>
          <p className="text-gray-400 mt-3 md:mt-4 text-base md:text-lg max-w-xl mx-auto">
            Tres herramientas de clase mundial. Un solo pago. Un año completo.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8">
          {tools.map((tool, i) => (
            <div
              key={i}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              className="group relative bg-[#111827] border border-[#1f2937] rounded-3xl overflow-hidden cursor-pointer transition-all duration-300"
              style={{
                borderColor: hovered === i ? tool.borderHover : undefined,
                boxShadow: hovered === i ? `0 0 40px ${tool.glow}` : undefined,
                transform: hovered === i ? 'translateY(-6px)' : undefined,
              }}
            >
              {/* Card Image */}
              <div className="relative w-full h-40 md:h-44 overflow-hidden">
                <img
                  src={tool.img}
                  alt={tool.name}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#111827]" />
              </div>

              {/* Card Content */}
              <div className="p-5 md:p-8">
                {/* Icon + Badge */}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-2xl bg-gradient-to-br ${tool.color} text-xl md:text-2xl`}>
                    {tool.icon}
                  </div>
                  <div>
                    <h3 className="text-white font-black text-lg md:text-xl leading-tight">{tool.name}</h3>
                    <span className={`text-xs font-bold bg-gradient-to-r ${tool.color} bg-clip-text text-transparent`}>
                      {tool.badge}
                    </span>
                  </div>
                </div>

                <p className="text-[#10b981] font-semibold text-sm mb-2 md:mb-3">{tool.tagline}</p>
                <p className="text-gray-400 text-sm leading-relaxed mb-4 md:mb-5">{tool.description}</p>

                {/* Features */}
                <ul className="space-y-1.5 md:space-y-2">
                  {tool.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2 text-gray-300 text-sm">
                      <span className="text-[#10b981] text-xs">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>

                {/* Bottom bar */}
                <div
                  className={`mt-5 md:mt-6 h-1 rounded-full bg-gradient-to-r ${tool.color} transition-all duration-300`}
                  style={{ width: hovered === i ? '100%' : '40%' }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Value summary */}
        <div className="mt-12 md:mt-16 text-center">
          <div className="inline-flex flex-wrap items-center justify-center gap-4 md:gap-8 bg-[#111827] border border-[#1f2937] rounded-2xl px-5 md:px-8 py-4 md:py-5">
            {[
              { label: 'Valor real', value: 'S/ 800+/año' },
              { label: 'Herramientas', value: '3 PRO' },
              { label: 'Dispositivos', value: 'Ilimitados' },
              { label: 'Duración', value: '1 Año' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-xl md:text-2xl font-black bg-gradient-to-r from-[#8b5cf6] to-[#10b981] bg-clip-text text-transparent">
                  {stat.value}
                </p>
                <p className="text-gray-500 text-xs mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
